'use client';

import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useStore } from '@/app/providers/store';
import { useChatDropzone } from '../../../_hook/use-chat-dropzone';
import { useClawFolder } from '../../../_hook/use-claw-folder';
import { DEFAULT_MEDIA_VIEW_MODE } from '../../../constants';
import type { InsertAssetPayload, MediaViewMode } from '../../../types';
import { MediaBrowserGrid } from './MediaBrowserGrid';
import { MediaBrowserTree } from './MediaBrowserTree';

interface MediaBrowserProps {
  onInsertAsset: (asset: InsertAssetPayload) => void;
  previewTargetFilePath?: string | null;
  onPreviewHandled?: () => void;
  /** 外层（Chat）正在探活/恢复 session 默认目录的窗口期 */
  syncing?: boolean;
}

export const MediaBrowser = ({
  onInsertAsset,
  previewTargetFilePath,
  onPreviewHandled,
  syncing,
}: MediaBrowserProps) => {
  const t = useTranslations();
  const { activeClawId, clawActive } = useStore((state) => ({
    activeClawId: state.activeClaw?.id,
    clawActive: state.clawStatus === 'CLAW_STATUS_ACTIVE',
  }));
  const [viewMode, setViewMode] = useState<MediaViewMode>(DEFAULT_MEDIA_VIEW_MODE);
  const clawFolderApi = useClawFolder(activeClawId);
  const containerRef = useRef<HTMLDivElement>(null);

  // drop 后暂存文件，由当前活跃子组件（Grid/Tree）通过 pendingDropFiles prop 消费并清空。
  const [pendingDropFiles, setPendingDropFiles] = useState<File[] | null>(null);
  const { isDraggingOver } = useChatDropzone({
    containerRef,
    onDrop: (files) => {
      if (files.length) setPendingDropFiles(files);
    },
    // claw 未激活时上传会失败，直接关掉拖拽入口避免无效的"释放上传"提示。
    enabled: !!activeClawId && clawActive,
  });

  const sharedProps = {
    activeClawId,
    clawFolderApi,
    viewMode,
    onViewModeChange: setViewMode,
    previewTargetFilePath,
    onPreviewHandled,
    syncing,
    pendingDropFiles,
    onPendingDropFilesHandled: () => setPendingDropFiles(null),
  };

  return (
    <div ref={containerRef} className="relative h-full min-h-0">
      {isDraggingOver && (
        <div className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-16 border-2 border-[#508DFF] border-dashed bg-[rgba(80,141,255,0.06)]">
          <span className="text-[#508DFF] text-base">{t('create.dragUpload.placeholder')}</span>
        </div>
      )}
      {viewMode === 'tree' ? (
        <MediaBrowserTree {...sharedProps} />
      ) : (
        <MediaBrowserGrid {...sharedProps} onInsertAsset={onInsertAsset} />
      )}
    </div>
  );
};
